import React, { useMemo, useState } from "react";
import CommonCollapsible from "../../../../components/common/CommonCollapsible";
import { LuClock, LuPlane } from "react-icons/lu";
import { IoMdArrowDropdown, IoMdArrowDropup } from "react-icons/io";
import { flightDirectionDuration } from "../../../../controllers/flightController";
import EditIcon from "@mui/icons-material/Edit";
import { IconButton } from "@mui/material";
import EditFlightSegmentModal from "../OrderDetail/modals/EditFlightSegmentModal";

function BookingConfirmationFlightDetails({
  data,
  allowEdit = false,
  onUpdateTicket,
}) {
  const [openIndex, setOpenIndex] = useState();
  const [editSegment, setEditSegment] = useState(null);

  const flightBooking = data?.flightBooking;
  const flights = flightBooking?.flights || [];

  const directions = useMemo(() => {
    const directionLib = {};
    const oldDirections = flights;

    const groupDirections = () => {
      const results = {};
      for (let index = 0; index < (oldDirections || []).length; index++) {
        const segment = oldDirections[index];
        const withIndex = { ...segment, __index: index };
        const direction = results[segment?.segment];
        if (direction) {
          results[segment?.segment] = [...direction, withIndex];
        } else {
          results[segment?.segment] = [withIndex];
        }
      }
      return Object.values(results);
    };
    const updatedDirections = groupDirections().map((direction) => {
      const firstSeg = direction[0];
      const lastSeg = direction[direction?.length - 1];
      const route = `${firstSeg?.departureLocation}-${lastSeg?.arrivalLocation}`;
      const changedDirection = directionLib[route];
      if (changedDirection) return changedDirection;
      return direction;
    });
    return updatedDirections || [];
  }, [flights]);

  return (
    <div className="w-full space-y-4 mt-4">
      <div className="flex items-center gap-2">
        <span className="font-bold text-xl"> Flight Details</span>
      </div>

      {directions.map((direction, i) => (
        <div
          key={i}
          className="bg-white rounded-xl shadow-sm p-4 flex flex-col w-full"
        >
          <BookingDetailsHeaderRow
            index={i}
            onClick={() =>
              i === openIndex ? setOpenIndex(null) : setOpenIndex(i)
            }
            direction={direction}
            open={i === openIndex}
          />

          <CommonCollapsible open={openIndex === i} duration={200}>
            <div className="flex w-full py-3">
              <div className="pt-3 flex flex-col gap-3 bg-[#B6CDF9]/10 w-full p-3">
                {direction.map((seg, j) => (
                  <div className="w-full flex flex-col" key={j}>
                    <div className="w-full hidden lg:flex text-sm items-center font-medium text-gray-800 mb-1">
                      <span className="">{seg?.flightNumber}</span>
                      <hr className="w-6 mx-1 h-px border-b border-gray-300" />
                      <span className="w-32 text-start mr-2">
                        {seg?.airlineName || seg?.airline?.image?.description}
                      </span>
                      <span>{seg?.cabinClass}</span>
                      <span className="bg-gray-200 rounded-md px-1 text-sm ml-2">
                        {seg?.bookingClass}
                      </span>
                      <div className="flex items-center gap-1 text-sm text-gray-600 ml-2">
                        <LuClock className="text-gray-500" />
                        <span>{seg?.duration}</span>
                      </div>
                      <div className="text-gray-500 text-sm ml-4">
                        Operated by{" "}
                        <span className="text-black">
                          {seg?.airlineName || seg?.airline?.image?.description}
                        </span>
                      </div>
                      <div className="flex-1" />
                      {allowEdit ? (
                        <IconButton
                          size="small"
                          aria-label="edit segment"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditSegment({
                              segment: seg,
                              index: seg?.__index,
                            });
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      ) : null}
                    </div>
                    <div className="w-full flex gap-1 flex-col lg:hidden text-sm items-center font-medium text-gray-800 mb-1">
                      <div className="flex w-full items-center justify-between">
                        <span className="">{seg?.flightNumber}</span>
                        <hr className="flex-1 mx-1 h-px border-b border-gray-300" />
                        <span className=" text-end mr-2">
                          {seg?.airlineName || seg?.airline?.image?.description}
                        </span>
                        {allowEdit ? (
                          <IconButton
                            size="small"
                            aria-label="edit segment"
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditSegment({
                                segment: seg,
                                index: seg?.__index,
                              });
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        ) : null}
                      </div>
                      <div className="flex w-full items-center">
                        <span>{seg?.cabinClass}</span>
                        <span className="bg-gray-200 rounded-md px-1 text-sm ml-2">
                          {seg?.bookingClass}
                        </span>
                        <div className="flex-1"></div>
                        <div className="flex items-center gap-1 text-sm text-gray-600 ml-2">
                          <LuClock className="text-gray-500" />
                          <span>{seg?.duration}</span>
                        </div>
                      </div>
                      <div className="text-gray-500 text-sm w-full">
                        Operated by{" "}
                        <span className="text-black">
                          {seg?.airlineName || seg?.airline?.image?.description}
                        </span>
                      </div>
                    </div>
                    <div className="flex lg:hidden mt-3"></div>
                    <div className="w-full hidden lg:flex text-sm items-center font-medium text-gray-800 mb-1">
                      <span className="w-20">
                        {seg?.departureTime || seg?.departure?.time}
                      </span>
                      <span className="w-32 text-gray-500">
                        {seg?.departureDate || seg?.departure?.date}
                      </span>
                      <span className="w-10 text-primary1">
                        {seg?.departureLocation || seg?.departure?.location}
                      </span>
                      <span className=" text-gray-500">
                        {seg?.departureAirport || seg?.departure?.airport}
                        {seg?.departureTerminal
                          ? ` · T${seg.departureTerminal}`
                          : ""}
                      </span>
                    </div>
                    <div className="w-full flex lg:hidden flex-col text-sm items-center font-medium text-gray-800 mb-1">
                      <div className="w-full flex">
                        <span className="w-20">
                          {seg?.departureTime || seg?.departure?.time}
                        </span>
                        <span className="w-32 text-gray-500">
                          {seg?.departureDate || seg?.departure?.date}
                        </span>
                        <span className="flex-1 text-primary1 text-end">
                          {seg?.departureLocation || seg?.departure?.location}
                        </span>
                      </div>
                      <span className="text-gray-500 text-end w-full">
                        {seg?.departureAirport || seg?.departure?.airport}
                        {seg?.departureTerminal
                          ? ` · T${seg.departureTerminal}`
                          : ""}
                      </span>
                    </div>
                    <div className="w-full hidden lg:flex text-sm items-center font-medium text-gray-800 mb-1">
                      <span className="w-20">
                        {seg?.arrivalTime || seg?.arrival?.time}
                      </span>
                      <span className="w-32 text-gray-500">
                        {seg?.arrivalDate || seg?.arrival?.date}
                      </span>
                      <span className="w-10 text-primary1">
                        {seg?.arrivalLocation || seg?.arrival?.location}
                      </span>
                      <span className=" text-gray-500">
                        {seg?.arrivalAirport || seg?.arrival?.airport}
                        {seg?.arrivalTerminal
                          ? ` · T${seg.arrivalTerminal}`
                          : ""}
                      </span>
                    </div>
                    <div className="w-full flex lg:hidden flex-col text-sm items-center font-medium text-gray-800 mb-1">
                      <div className="w-full flex">
                        <span className="w-20">
                          {seg?.arrivalTime || seg?.arrival?.time}
                        </span>
                        <span className="w-32 text-gray-500">
                          {seg?.arrivalDate || seg?.arrival?.date}
                        </span>
                        <span className="flex-1 text-primary1 text-end">
                          {seg?.arrivalLocation || seg?.arrival?.location}
                        </span>
                      </div>
                      <span className="text-gray-500 text-end w-full">
                        {seg?.arrivalAirport || seg?.arrival?.airport}
                        {seg?.arrivalTerminal
                          ? ` · T${seg.arrivalTerminal}`
                          : ""}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </CommonCollapsible>
        </div>
      ))}

      {allowEdit ? (
        <EditFlightSegmentModal
          open={!!editSegment}
          setOpen={(value) => {
            if (!value) setEditSegment(null);
          }}
          segment={editSegment?.segment}
          segmentIndex={editSegment?.index}
          flightBooking={flightBooking}
          onUpdateTicket={onUpdateTicket}
        />
      ) : null}
    </div>
  );
}

export default BookingConfirmationFlightDetails;

function BookingDetailsHeaderRow({
  index = 0,
  onClick,
  open,
  direction,
  routeChange,
}) {
  const firstSegemt = direction[0];
  const lastSegment = direction[direction?.length - 1];
  const logo = firstSegemt?.airlineImage || firstSegemt?.airline?.image?.url;
  const directionDuration = flightDirectionDuration(
    direction?.map((segment) => {
      if (!segment?.departure) {
        segment.departure = {
          time: segment?.departureTime,
          date: segment?.departureDate,
        };
      }
      if (!segment?.arrival) {
        segment.arrival = {
          time: segment?.arrivalTime,
          date: segment?.arrivalDate,
        };
      }
      return segment;
    }) || [],
  );

  return (
    <div
      className="w-full flex lg:items-center gap-3 flex-col lg:flex-row justify-between cursor-pointer"
      onClick={onClick}
    >
      <div className="flex lg:items-center gap-3 min-w-0 flex-col lg:flex-row">
        <div className="w-6 h-6  rounded-md bg-primary1 text-white text-xs font-semibold hidden lg:flex items-center justify-center">
          {index + 1}
        </div>

        <img src={logo} alt="" className="w-12 lg:w-6 h-6 object-contain" />

        <div className="flex items-start flex-col min-w-0">
          <div className="text-sm text-gray-800 min-w-0">
            <span className="truncate">
              {firstSegemt?.departureLocationName ||
                firstSegemt?.departure?.city}{" "}
              <a className="text-primary1 hover:underline" href="#">
                {firstSegemt?.departureLocation ||
                  firstSegemt?.departure?.location}
              </a>{" "}
              —{" "}
              {lastSegment?.arrivalLocationName ||
                lastSegment?.arrival?.location}{" "}
              <a className="text-primary1 hover:underline" href="#">
                {lastSegment?.arrivalLocation ||
                  lastSegment?.arrivalLocationName}
              </a>
            </span>
          </div>
          <div className="flex lg:items-center gap-1 lg:gap-4 flex-col lg:flex-row">
            <div className="text-sm text-gray-900">
              <span className="font-semibold">
                {firstSegemt?.departureTime || firstSegemt?.departure?.time}
              </span>{" "}
              {firstSegemt?.departureDate || firstSegemt?.departure?.time} —{" "}
              <span className="font-semibold">
                {lastSegment?.arrivalTime || lastSegment?.arrival?.time}
              </span>{" "}
              {lastSegment?.arrivalDate || lastSegment?.arrival?.date}
            </div>

            <div className="flex items-center gap-1 text-sm text-gray-600">
              <LuClock className="text-gray-500" />
              <span>{directionDuration}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 w-full lg:w-fit justify-end lg:justify-normal">
        <span className="rounded-md border border-primary1/40 bg-primary1/10 text-primary1 text-xs px-3 py-1">
          Details
        </span>
        {open ? (
          <IoMdArrowDropup className="text-lg" />
        ) : (
          <IoMdArrowDropdown className="text-lg" />
        )}
      </div>
    </div>
  );
}
